import * as React from "react";
import styled from "styled-components";
import { ScrollContainerProps } from "./ScrollContainer";

export interface ChildOptions<T> {
  properties: T;
}

interface Props {
  beforeChange?(nextItem: any): void;
  afterChange?(): void;
}

interface State {
  currentChild: number;
}

const Wrapper = styled.div`
  height: 100%;
`;

class ScrollWrapper extends React.Component<Props, State> {
  private _childrenCount: number;
  private _canScroll: boolean;
  private _scrollValues: any[number];
  private _scrollStartTime: number;
  private _childrenOptions: ChildOptions<{}>[];

  state: State = {
    currentChild: 0
  };

  constructor(props: any) {
    super(props);
    this._childrenCount = React.Children.count(props.children);
    this._childrenOptions = [];
    this._canScroll = true;
    this._scrollValues = [];
    this._scrollStartTime = new Date().getTime();

    this.setChildOptions();
    this.addMouseWheelEventHandlers();
  }

  componentWillUnmount() {
    this.removeMouseWheelEventHandlers();
  }

  setChildOptions = () => {
    const { props } = this;

    React.Children.map(
      props.children,
      (container: React.ReactElement<ScrollContainerProps>) => {
        const containerProps = Object.keys(container.props)
          .filter(property => property.includes("data"))
          .reduce((previous: any, current: any) => {
            previous[current] = container.props[current];
            return previous;
          }, {});

        React.Children.map(
          container.props.children,
          (section: React.ReactElement<ScrollContainerProps>) => {
            const sectionProps = Object.keys(section.props)
              .filter(property => property.includes("data"))
              .reduce((previous: any, current: any) => {
                previous[current] = section.props[current];
                return previous;
              }, {});

            this._childrenOptions.push({
              properties: {...containerProps, ...sectionProps}
            });
          }
        );
      }
    );
  };

  addMouseWheelEventHandlers = () => {
    const { handleMouseWheel } = this;

    if (typeof window !== "undefined") {
      window.addEventListener("mousewheel", handleMouseWheel, true);
      window.addEventListener("DOMMouseScroll", handleMouseWheel, true);
    }

  };

  removeMouseWheelEventHandlers = () => {
    const { handleMouseWheel } = this;

    window.removeEventListener("mousewheel", handleMouseWheel, true);
    window.removeEventListener("DOMMouseScroll", handleMouseWheel, true);
  };

  handleMouseWheel = (event: WheelEvent) => {
    const {
      _childrenCount,
      _scrollValues,
      _canScroll,
      updateScrollValues,
      clearScrollValues,
      getAverage,
      nextChild
    } = this;
    const { currentChild } = this.state;
    const e = (window.event as WheelEvent) || event; // old IE support
    const delta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail));
    const updatedChild = currentChild - delta;

    // Max child, Min child
    if (updatedChild > _childrenCount + 1 || updatedChild < 0) {
      return false;
    }

    const value = e.wheelDelta || -e.deltaY || -e.detail;
    updateScrollValues(value);
    clearScrollValues();

    const averageEnd = getAverage(_scrollValues, 10);
    const averageMiddle = getAverage(_scrollValues, 70);
    const isAccelerating = averageEnd >= averageMiddle;

    if (_canScroll == false || isAccelerating == false) {
      return false;
    }

    nextChild(delta);

    return delta;
  };

  updateScrollValues = (value: number) => {
    const { _scrollValues } = this;
    _scrollValues.push(Math.abs(value));

    // Max length
    if (_scrollValues.length > 149) {
      this._scrollValues.shift();
    }
  };

  clearScrollValues = () => {
    const { _scrollStartTime } = this;
    const currentTime = new Date().getTime();
    const different = currentTime - _scrollStartTime;

    this._scrollStartTime = currentTime;

    if (different > 200) {
      this._scrollValues = [];
    }
  };

  getAverage(elements: number[], number: number) {
    let sum = 0;
    const lastElements = elements.slice(Math.max(elements.length - number, 1));

    for (let i = 0; i < lastElements.length; i++) {
      sum = sum + lastElements[i];
    }

    return Math.ceil(sum / number);
  }

  nextChild = (delta: number) => {
    const { beforeChange } = this.props;
    const { currentChild } = this.state;
    const nextChild = currentChild - delta;
    const nextChildOptions = this._childrenOptions[nextChild];

    console.log(this._childrenOptions);

    !!beforeChange && beforeChange(nextChildOptions);

    this._canScroll = false;
    this.setState({
      currentChild: nextChild
    });
  };

  transitionEnd = () => {
    const { afterChange } = this.props;

    !!afterChange && afterChange();

    this._canScroll = true;
  };

  render() {
    const { transitionEnd } = this;
    const { children } = this.props;
    const { currentChild } = this.state;

    const childrenWithProps = React.Children.map(children, child =>
      React.cloneElement(child as any, {
        index: currentChild,
        transitionEnd: transitionEnd
      })
    );

    return <Wrapper>{childrenWithProps}</Wrapper>;
  }
}

export default ScrollWrapper;



// WEBPACK FOOTER //
// ./src/components/ScrollSection/ScrollWrapper.tsx